<script>
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import SidebarLink from '@/components/layouts/dashboard/SidebarLink';
import SidebarLinkGroup from '@/components/layouts/dashboard/SidebarLinkGroup';
import Timeline from '@/components/timeline/Timeline';
import TimelineEvent from '@/components/timeline/TimelineEvent';

export default {
  name: 'User',
  components: {
    DashboardLayout,
    SidebarLink,
    SidebarLinkGroup,
    Timeline,
    TimelineEvent
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: '',
      breadcrumbs: [],
      statusCode: null,
      revisions: [],
      user: {}
    };
  },
  created() {
    // Fetch the user
    Promise.all([
      this.$http.get(`/users/${this.$route.params.userId}`),
      this.$http.get(`/users/${this.$route.params.userId}/revisions`)
    ])
      .then(res => {
        this.user = res[0].data.user;
        this.revisions = res[1].data.revisions;
        this.title = this.user.name;
        this.breadcrumbs = [
          { text: this.$t('Users'), to: { name: 'Users' }},
          { text: this.user.name }
        ];
        this.statusCode = 200;
      })
      .catch(err => this.statusCode = err.response.status);
  }
};
</script>

<template>
  <dashboard-layout :status-code="statusCode" size="medium">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Sidebar -->
    <template slot="sidebar">
      <!-- Actions -->
      <sidebar-link-group :title="$t('Actions')">
        <sidebar-link
          :text="$t('Edit User')"
          :to="{ name: 'EditUser', params: { userId: user.id }}"
          icon="el-icon-fa-cog"
        />
      </sidebar-link-group>
    </template>

    <div class="user">
      <div class="user-avatar">
        <img
          :src="user.avatar ? user.avatar : require('@/assets/user-light.svg')"
          :alt="user.name"
        >
      </div>
      <div class="user-info">
        <h2 class="user-name">{{ user.name }}</h2>
        <div class="user-email"><a :href="`mailto:${user.email}`">{{ user.email }}</a></div>
        <div class="user-last-seen">
          <span v-if="user.lastSeen">
            {{ $t('Last seen {date}', { date: $d(new Date(user.lastSeen), 'medium') }) }}
          </span>
          <span v-else>
            {{ $t('Never logged in') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Timeline -->
    <timeline>
      <timeline-event
        v-for="revision in revisions"
        :key="revision.id"
        :date="$d(new Date(revision.createdAt), 'short')"
      >
        <template slot="title">
          <span class="timeline-avatar">
            <img
              :src="revision.user.avatar ? revision.user.avatar : require('@/assets/user-light.svg')"
              :alt="revision.user.name"
            >
          </span>
          {{ revision.isDraft ? $t('Saved a Draft') : $t('Published Changes') }}
        </template>

        <i18n
          :path="
            revision.isDraft ?
              '{user} saved a draft of {page} on {site}' :
              '{user} published changes to {page} on {site}'
          "
          tag="span"
        >
          <router-link
            :to="{ name: 'User', params: { userId: revision.user.id }}"
            place="user"
          >
            {{ revision.user.name }}
          </router-link>
          <router-link
            :to="{ name: 'Site', params: { siteId: revision.site.id }}"
            place="site"
          >
            {{ revision.site.name }}
          </router-link>
          <router-link
            :to="{ name: 'EditPage', params: { siteId: revision.site.id, pageId: revision.page.id }}"
            place="page"
          >
            {{ revision.page.name }}
          </router-link>
        </i18n>
      </timeline-event>
    </timeline>
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.user {
  text-align: center;
  margin: 2rem 0 4rem 0;

  .user-avatar {
    margin-bottom: .5rem;

    img {
      display: block;
      width: 10rem;
      height: 10rem;
      border-radius: 50%;
      margin: 0 auto;
    }
  }

  .user-info {
    .user-name {
      font-size: 1.6rem;
      margin-bottom: .5rem;
    }

    .user-email {
      margin-bottom: .5rem;
    }

    .user-last-seen {
      font-size: $--font-size-small;
      font-style: italic;
      color: $--color-gray-40;
    }
  }
}

.timeline-avatar {
  img {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    vertical-align: middle;
    margin-right: .125rem;
  }
}
</style>
